import Close from '@mui/icons-material/Close';
import type { AlertColor } from '@mui/material';
import { Alert, alpha, Box, Button, Divider, IconButton } from '@mui/material';
import { animate, motion, useMotionValue } from 'motion/react';
import type {
  CustomContentProps,
  SnackbarKey,
  SnackbarProviderProps,
  TransitionProps,
} from 'notistack';
import {
  closeSnackbar,
  SnackbarContent,
  SnackbarProvider as NotiStackProvider,
} from 'notistack';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const VARIANT_SEVERITY: Record<
  CustomContentProps['variant'],
  AlertColor | null
> = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
  default: null,
};

// Springy enter from below with a touch of overshoot. Exit is position-aware:
//
// - hiddenSlide: the bottom-most toast slides straight down behind the screen's
//   bottom edge. y: '150%' translates it fully below its own height so the
//   viewport edge clips it. Opacity stays 1 so it reads as sliding under the
//   edge, not dissolving. Snappy spring with bounce: 0 (no overshoot tail) since
//   notistack only unmounts once the animation completes.
// - hiddenFade: toasts higher in a stack can't reach the screen edge without
//   sliding over the siblings beneath them (notistack keeps siblings in place
//   until exit completes), so they fade out with a small downward drift instead.
const TRANSITION_VARIANTS = {
  initial: { opacity: 0, y: 48, scaleX: 0.95, scaleY: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scaleX: 1,
    scaleY: 1,
    transition: { type: 'spring', visualDuration: 0.3, bounce: 0.25 },
  },
  hiddenSlide: {
    opacity: 1,
    y: '150%',
    scaleX: 1,
    scaleY: 1,
    transition: { type: 'spring', visualDuration: 0.2, bounce: 0 },
  },
  hiddenFade: {
    opacity: 0,
    y: 8,
    scaleX: 0.98,
    scaleY: 0.98,
    transition: { duration: 0.15, ease: 'easeIn' },
  },
} as const;

// The container is flex-direction: column-reverse, so the container's first
// child renders at the visual bottom. Only that toast can slide off the bottom
// edge without crossing the toasts beneath it; everything higher fades instead.
export function resolveExitVariant(
  node: Element | null
): 'hiddenSlide' | 'hiddenFade' {
  const container = node?.closest('.notistack-SnackbarContainer');
  if (!container) return 'hiddenSlide';
  const wrapper = Array.from(container.children).find((child) =>
    child.contains(node)
  );
  return wrapper === container.firstElementChild ? 'hiddenSlide' : 'hiddenFade';
}

// notistack transition contract: animate on `in`, then report back via
// onEntered/onExited. onExited is load-bearing: notistack only unmounts the
// snackbar once it fires.
const SpringSnackbarTransition = forwardRef<
  HTMLDivElement,
  TransitionProps & { children: React.ReactElement }
>(function SpringSnackbarTransition(props, ref) {
  const {
    children,
    in: isIn = false,
    style,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
  } = props;
  const nodeRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => nodeRef.current!);
  const [exitVariant, setExitVariant] = useState<'hiddenSlide' | 'hiddenFade'>(
    'hiddenSlide'
  );

  // Layout effect (not a plain effect) so the exit variant is resolved before
  // the browser paints, the toast never flashes the wrong exit direction.
  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    if (isIn) {
      onEnter?.(node, true);
      onEntering?.(node, true);
    } else {
      setExitVariant(resolveExitVariant(node));
      onExit?.(node);
      onExiting?.(node);
    }
    // Only react to in-state flips; the callbacks are stable per snackbar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIn]);

  return (
    <motion.div
      ref={nodeRef}
      style={style}
      initial="initial"
      animate={isIn ? 'visible' : exitVariant}
      variants={TRANSITION_VARIANTS}
      onAnimationComplete={(definition) => {
        const node = nodeRef.current;
        if (!node) return;
        if (definition === 'visible') onEntered?.(node, true);
        else onExited?.(node);
      }}
    >
      {children}
    </motion.div>
  );
});

const AppSnackbarContent = forwardRef<HTMLDivElement, CustomContentProps>(
  function AppSnackbarContent(props, ref) {
    const {
      id,
      message,
      variant,
      action: actionProp,
      style,
      className,
    } = props;
    const severity = VARIANT_SEVERITY[variant];
    const action =
      typeof actionProp === 'function' ? actionProp(id) : actionProp;

    return (
      <SnackbarContent ref={ref} style={style} className={className}>
        <Alert
          severity={severity ?? 'info'}
          icon={severity ? undefined : false}
          variant="filled"
          action={
            <>
              {action}
              {action != null && (
                <Divider
                  orientation="vertical"
                  flexItem
                  aria-hidden
                  sx={{
                    mx: 1,
                    my: 0.5,
                    borderColor: 'currentColor',
                    opacity: 0.3,
                  }}
                />
              )}
              <IconButton
                size="small"
                color="inherit"
                aria-label="Close"
                onClick={() => closeSnackbar(id)}
              >
                <Close fontSize="small" />
              </IconButton>
            </>
          }
          sx={(theme) => {
            const shadowColor = theme.palette.common.black;
            const [ambient, key, contact] =
              theme.palette.mode === 'dark'
                ? [0.45, 0.4, 0.3]
                : [0.16, 0.11, 0.08];
            return {
              width: '100%',
              minWidth: 320,
              alignItems: 'center',
              '& .MuiAlert-action': { alignItems: 'center', pt: 0 },
              boxShadow: [
                `0 16px 36px -8px ${alpha(shadowColor, ambient)}`,
                `0 6px 16px -4px ${alpha(shadowColor, key)}`,
                `0 1px 3px ${alpha(shadowColor, contact)}`,
              ].join(', '),
              ...(severity
                ? {}
                : {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                  }),
            };
          }}
        >
          {message}
        </Alert>
      </SnackbarContent>
    );
  }
);

const UNDO_RING_SIZE = 18;
const UNDO_RING_RADIUS = 7;

interface UndoSnackbarActionProps {
  snackbarId: SnackbarKey;
  onUndo: () => void;
  // Fired when the timer runs out without the user undoing.
  onExpire?: () => void;
  durationMs?: number;
}

// Countdown ring + Undo button for undoable snackbars. Enqueue the snackbar
// with `persist: true`; this component owns the dismiss timing so the
// countdown ring and the toast lifetime can never drift apart. Hovering or
// focusing the button pauses the timer.
export function UndoSnackbarAction({
  snackbarId,
  onUndo,
  onExpire,
  durationMs = 5000,
}: UndoSnackbarActionProps) {
  const progress = useMotionValue(1);
  const animationRef = useRef<ReturnType<typeof animate> | null>(null);
  const isDoneRef = useRef(false);
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.ceil(durationMs / 1000)
  );

  useEffect(() => {
    return progress.on('change', (latest) => {
      setSecondsLeft(Math.max(0, Math.ceil((latest * durationMs) / 1000)));
    });
  }, [progress, durationMs]);

  useEffect(() => {
    const animation = animate(progress, 0, {
      duration: durationMs / 1000,
      ease: 'linear',
      onComplete: () => {
        if (isDoneRef.current) return;
        isDoneRef.current = true;
        closeSnackbar(snackbarId);
        onExpire?.();
      },
    });
    animationRef.current = animation;
    return () => animation.stop();
    // Restarting the countdown on re-render would let an expired action fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUndo = () => {
    if (isDoneRef.current) return;
    isDoneRef.current = true;
    animationRef.current?.stop();
    closeSnackbar(snackbarId);
    onUndo();
  };

  return (
    <Button
      color="inherit"
      size="small"
      onClick={handleUndo}
      onPointerEnter={() => animationRef.current?.pause()}
      onPointerLeave={() => animationRef.current?.play()}
      onFocus={() => animationRef.current?.pause()}
      onBlur={() => animationRef.current?.play()}
      startIcon={
        <svg
          width={UNDO_RING_SIZE}
          height={UNDO_RING_SIZE}
          viewBox={`0 0 ${UNDO_RING_SIZE} ${UNDO_RING_SIZE}`}
          style={{ transform: 'rotate(-90deg)' }}
          aria-hidden
        >
          <circle
            cx={UNDO_RING_SIZE / 2}
            cy={UNDO_RING_SIZE / 2}
            r={UNDO_RING_RADIUS}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.3}
            strokeWidth={2}
          />
          <motion.circle
            cx={UNDO_RING_SIZE / 2}
            cy={UNDO_RING_SIZE / 2}
            r={UNDO_RING_RADIUS}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            style={{ pathLength: progress }}
          />
        </svg>
      }
    >
      Undo
      <Box
        component="span"
        aria-hidden
        sx={{
          ml: 0.75,
          fontVariantNumeric: 'tabular-nums',
          textTransform: 'none',
          opacity: 0.75,
        }}
      >
        {secondsLeft}s
      </Box>
    </Button>
  );
}

export const SnackbarProvider = ({
  children,
  ...rest
}: Omit<SnackbarProviderProps, 'Components'>) => {
  return (
    <NotiStackProvider
      Components={{
        info: AppSnackbarContent,
        warning: AppSnackbarContent,
        error: AppSnackbarContent,
        default: AppSnackbarContent,
        success: AppSnackbarContent,
      }}
      TransitionComponent={SpringSnackbarTransition}
      {...rest}
    >
      {children}
    </NotiStackProvider>
  );
};
